package org.zest.insan_360_website.data.page_states.dashboard

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.model.AdminCreditsDashPayload
import org.zest.insan_360_website.model.AdminCreditsDashRequest
import org.zest.insan_360_website.model.AdminRequest
import org.zest.insan_360_website.model.SupAdmCreditsDashPayload
import org.zest.insan_360_website.model.SupAdmPaymentsDashRequest
import org.zest.insan_360_website.model.SuperAdminRequest
import org.zest.insan_360_website.model.UserRole
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.convertToIsoDate
import org.zest.insan_360_website.utils.getPastDateString
import org.zest.insan_360_website.utils.getTodayDateString
import org.zest.insan_360_website.utils.processResponse

object CreditsDashboardState {
    var creditDashStats by mutableStateOf<AdminCreditsDashPayload?>(null)
    var paymentDashStats by mutableStateOf<SupAdmCreditsDashPayload?>(null)
    var isLoading by mutableStateOf(false)

    var supAdmStartDate by mutableStateOf<String?>(getPastDateString(30))
    var supAdmEndDate by mutableStateOf<String?>(getTodayDateString())
    var supAdmCurrentPage by mutableStateOf(1)
    var supAdmPaymentFilter by mutableStateOf("BOTH")

    fun reset() {
        creditDashStats = null
        paymentDashStats = null
        isLoading = false
        supAdmStartDate = getPastDateString(30)
        supAdmEndDate = getTodayDateString()
        supAdmCurrentPage = 1
        supAdmPaymentFilter = "BOTH"
    }

    fun handleSupAdmStartDateChange(newValue: String) {
        val cleanVal = newValue.ifBlank { null }
        if (cleanVal != null && supAdmEndDate != null && cleanVal > supAdmEndDate!!) {
            supAdmStartDate = cleanVal
            supAdmEndDate = cleanVal
        } else {
            supAdmStartDate = cleanVal
        }
        supAdmCurrentPage = 1
    }

    fun handleSupAdmEndDateChange(newValue: String) {
        val cleanVal = newValue.ifBlank { null }
        if (cleanVal != null && supAdmStartDate != null && cleanVal < supAdmStartDate!!) {
            supAdmEndDate = cleanVal
            supAdmStartDate = cleanVal
        } else {
            supAdmEndDate = cleanVal
        }
        supAdmCurrentPage = 1
    }

    fun fetchCreditStatistics(scope: CoroutineScope) {
        val role = AuthState.role
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        if (role != UserRole.ADMIN) return

        isLoading = true

        scope.launch {
            try {
                val request = AdminRequest(
                    email = email,
                    role = role,
                    request = AdminCreditsDashRequest(
                        email = email,
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/admin/dashboard/credits", request)
                    ?: throw Exception("Session expired. Please log in again.")

                creditDashStats = processResponse(response, fallbackErrorLog = "Error fetching credit statistics")

            } catch (e: Exception) {
                console.error("Network error fetching credit statistics: ${e.message}")
                creditDashStats = null
            } finally {
                isLoading = false
            }
        }
    }

    fun fetchSupAdmPaymentsList(
        scope: CoroutineScope,
        startDate: String? = null,
        endDate: String? = null,
        status: String? = null,
        page: Int = 1,
        limit: Int = 10
    ) {
        val role = AuthState.role
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        if (role != UserRole.SUPER_ADMIN) return

        isLoading = true

        scope.launch {
            try {
                val request = SuperAdminRequest(
                    email = email,
                    role = role,
                    request = SupAdmPaymentsDashRequest(
                        startDate = startDate?.let { kotlinx.datetime.Instant.parse(convertToIsoDate(it)!!) },
                        endDate = endDate?.let { kotlinx.datetime.Instant.parse(convertToIsoDate(it, true)!!) },
                        status = status,
                        page = page,
                        limit = limit
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/super-admin/dashboard/payments", request)
                    ?: throw Exception("Session expired. Please log in again.")

                paymentDashStats = processResponse(response, fallbackErrorLog = "Error fetching payment statistics")

            } catch (e: Exception) {
                console.error("Network error fetching payment statistics: ${e.message}")
                paymentDashStats = null
            } finally {
                isLoading = false
            }
        }
    }
}